import { useState, useEffect, FormEvent } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Alert } from '@/components/ui/Alert';
import { validateProjectForm, type ProjectFormData } from '@/utils/validation';
import { parseLocalDate } from '@/utils/dates';
import type { Database } from '@/types/database.types';

type Project = Database['public']['Tables']['projects']['Row'];

interface ProjectFormProps {
    /** Whether the form modal is open */
    isOpen: boolean;
    /** Callback when form is closed/cancelled */
    onClose: () => void;
    /** Callback when form is submitted with valid data */
    onSubmit: (data: ProjectFormData) => void;
    /** Existing project data for edit mode (undefined for create mode) */
    initialData?: Project;
    /** Whether the submit action is in progress */
    isLoading?: boolean;
    /** Error message from submission */
    error?: string | null;
}

/**
 * Project form component for creating and editing projects
 * Handles both create and edit modes based on presence of initialData
 */
export const ProjectForm = ({
    isOpen,
    onClose,
    onSubmit,
    initialData,
    isLoading = false,
    error = null,
}: ProjectFormProps) => {
    const isEditMode = !!initialData;

    // Form state
    const [formData, setFormData] = useState<ProjectFormData>({
        name: initialData?.name ?? '',
        committed_delivery_date: initialData?.committed_delivery_date ?? '',
    });

    // Validation errors
    const [errors, setErrors] = useState<{
        name?: string;
        committed_delivery_date?: string;
    }>({});

    // Sync form state when the form opens or the target project changes.
    // Keyed on `initialData?.id` (not the object reference) so background re-fetches
    // that return the same project don't reset unsaved user edits mid-session.
    useEffect(() => {
        if (isOpen) {
            setFormData({
                name: initialData?.name ?? '',
                committed_delivery_date: initialData?.committed_delivery_date ?? '',
            });
            setErrors({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, initialData?.id]);

    // Handle input changes
    const handleChange = (field: keyof ProjectFormData, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        // Clear error for this field when user starts typing
        setErrors((prev) => ({ ...prev, [field]: undefined }));
    };

    // Handle form submission
    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();

        // Validate form
        const validation = validateProjectForm(formData, isEditMode);

        if (!validation.isValid) {
            setErrors(validation.errors);
            return;
        }

        // Submit valid data
        onSubmit({
            name: formData.name.trim(),
            committed_delivery_date: formData.committed_delivery_date,
        });
    };

    // Reset form when modal opens/closes
    const handleClose = () => {
        setFormData({
            name: initialData?.name ?? '',
            committed_delivery_date: initialData?.committed_delivery_date ?? '',
        });
        setErrors({});
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={isEditMode ? 'Edit Project' : 'Create New Project'}
        >
            <form onSubmit={handleSubmit} className="space-y-4">
                {/* API Error Alert */}
                {error && (
                    <Alert variant="error" className="mb-4">
                        {error}
                    </Alert>
                )}

                {/* Project Name Field */}
                <div>
                    <label htmlFor="project-name" className="block text-sm font-medium text-gray-700 mb-1">
                        Project Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                        id="project-name"
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        placeholder="Enter project name"
                        maxLength={100}
                        disabled={isLoading}
                        aria-invalid={!!errors.name}
                        aria-describedby={errors.name ? 'name-error' : undefined}
                        className="w-full"
                    />
                    {errors.name && (
                        <p id="name-error" className="mt-1 text-sm text-red-600">
                            {errors.name}
                        </p>
                    )}
                </div>

                {/* Committed Delivery Date Field */}
                <div>
                    <label
                        htmlFor="committed-date"
                        className="block text-sm font-medium text-gray-700 mb-1"
                    >
                        Committed Delivery Date <span className="text-red-500">*</span>
                    </label>
                    <Input
                        id="committed-date"
                        type="date"
                        value={formData.committed_delivery_date}
                        onChange={(e) => handleChange('committed_delivery_date', e.target.value)}
                        disabled={isLoading}
                        aria-invalid={!!errors.committed_delivery_date}
                        aria-describedby={errors.committed_delivery_date ? 'date-error' : undefined}
                        className="w-full"
                    />
                    {errors.committed_delivery_date && (
                        <p id="date-error" className="mt-1 text-sm text-red-600">
                            {errors.committed_delivery_date}
                        </p>
                    )}
                </div>

                {/* Past date warning in edit mode */}
                {isEditMode &&
                    formData.committed_delivery_date &&
                    parseLocalDate(formData.committed_delivery_date) < (() => { const t = new Date(); t.setHours(0,0,0,0); return t; })() && (
                    <Alert variant="warning">
                        Committed date is in the past. The project may be overdue.
                    </Alert>
                )}

                {/* Form Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                    <Button type="button" onClick={handleClose} variant="secondary" disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button type="submit" variant="primary" disabled={isLoading} aria-busy={isLoading}>
                        {isLoading ? 'Saving...' : isEditMode ? 'Update Project' : 'Create Project'}
                    </Button>
                </div>
            </form>
        </Modal>
    );
};

